import React, { useEffect } from 'react';
import { Shield, BookOpen, Code, Youtube, ExternalLink } from 'lucide-react';
import Navbar from '@/components/Navbar';
import SectionTransition from '@/components/SectionTransition';

const About: React.FC = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />

      <main className="flex-grow pt-24 pb-16">
        {/* Banner Section */}
        <SectionTransition>
          <div className="relative w-full h-[350px] overflow-hidden group">
            <img
              src="/b2t.png"
              alt="Bit2Terabyte Banner"
              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-background via-background/20 to-transparent"></div>
            <div className="absolute inset-x-0 bottom-0 py-12 px-4">
              <div className="container mx-auto text-center">
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight mb-4">Bit2Terabyte</h1>
                <p className="text-lg md:text-xl text-foreground/80 max-w-2xl mx-auto">
                  Protecting the digital frontier through technical mastery and strategic vision.
                </p>
              </div>
            </div>
          </div>
        </SectionTransition>

        {/* Introduction */}
        <section className="section-container mt-12">
          <div className="max-w-4xl mx-auto">
            <SectionTransition>
              <div className="glass-card p-8 rounded-xl border border-primary/10 shadow-xl shadow-primary/5">
                <p className="text-base md:text-lg text-foreground/90 leading-relaxed">
                  Bit2Terabyte is a specialized hub dedicated to showcasing cutting-edge cybersecurity solutions, interactive demos, and high-impact educational resources.
                </p>
              </div>
            </SectionTransition>
          </div>
        </section>

        {/* YouTube Channel Section */}
        <section className="section-container">
          <div className="max-w-4xl mx-auto">
            <SectionTransition delay={100}>
              <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#FF0000]/10 to-primary/10 p-1">
                <div className="glass-card p-8 md:p-12 rounded-[calc(1rem-1px)] flex flex-col md:flex-row items-center justify-between gap-8">
                  <div className="flex-1 text-center md:text-left">
                    <div className="flex items-center justify-center md:justify-start mb-4">
                      <div className="bg-[#FF0000] p-3 rounded-full text-white mr-4 animate-pulse">
                        <Youtube className="h-8 w-8" />
                      </div>
                      <h2 className="text-2xl md:text-3xl font-bold">Bit2Terabyte YouTube</h2>
                    </div>
                    <p className="text-muted-foreground mb-6 text-lg">
                      Visual deep-dives into security architecture, live exploit demos, and educational content to help you scale securely.
                    </p>
                    <a
                      href="https://www.youtube.com/@bit2terabyte"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center px-6 py-3 bg-[#FF0000] hover:bg-[#CC0000] text-white font-bold rounded-lg transition-all duration-300 shadow-lg shadow-[#FF0000]/20 group"
                    >
                      Subscribe & Learn
                      <ExternalLink className="h-4 w-4 ml-2 group-hover:translate-x-1 transition-transform" />
                    </a>
                  </div>
                  <div className="w-full md:w-48 lg:w-64 aspect-square bg-black/5 rounded-xl flex items-center justify-center border border-white/10 group overflow-hidden relative">
                    <img
                      src="/b2t.png"
                      alt="Channel Logo"
                      className="w-3/4 h-3/4 object-contain transition-transform duration-500 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-[#FF0000]/5 opacity-0 group-hover:opacity-100 transition-opacity"></div>
                  </div>
                </div>
              </div>
            </SectionTransition>
          </div>
        </section>

        {/* The Vision */}
        <section className="section-container">
          <div className="max-w-4xl mx-auto">
            <SectionTransition delay={100}>
              <div className="text-center mb-8">
                <h2 className="section-title">The Vision</h2>
              </div>
            </SectionTransition>
            <SectionTransition delay={200}>
              <div className="glass-card p-8 rounded-xl border border-primary/5">
                <p className="text-base md:text-lg text-foreground/90 leading-relaxed">
                  In an era where digital infrastructure is the backbone of the global economy, security cannot be an afterthought. Bit2Terabyte was created to demonstrate how strategic product management, fused with deep technical mastery, can transform complex security challenges into resilient, business-enabling solutions.
                </p>
              </div>
            </SectionTransition>
          </div>
        </section>

        {/* What You Will Find Here */}
        <section className="section-container">
          <SectionTransition>
            <div className="text-center mb-12">
              <h2 className="section-title">What You Will Find Here</h2>
              <p className="section-subtitle">More Than a Portfolio</p>
            </div>
          </SectionTransition>

          <div className="max-w-5xl mx-auto">
            <SectionTransition delay={100}>
              <div className="glass-card p-8 rounded-xl mb-6 border border-primary/5">
                <p className="text-base md:text-lg text-foreground/90 leading-relaxed text-center">
                  Bit2Terabyte is more than a portfolio; it is a resource for professionals and organizations seeking to stay ahead of the threat landscape:
                </p>
              </div>
            </SectionTransition>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Cybersecurity Solutions */}
              <SectionTransition delay={200}>
                <div className="glass-card p-6 rounded-xl h-full border border-primary/5 hover:border-primary/20 transition-all">
                  <div className="bg-primary/10 p-4 rounded-lg text-primary w-fit mb-4">
                    <Shield className="h-8 w-8" />
                  </div>
                  <h3 className="text-xl font-semibold mb-3">Cybersecurity Solutions</h3>
                  <p className="text-foreground/90 leading-relaxed">
                    Deep dives into architecting secure, scalable, and resilient cloud and telco infrastructure.
                  </p>
                </div>
              </SectionTransition>

              {/* Interactive Demos */}
              <SectionTransition delay={300}>
                <div className="glass-card p-6 rounded-xl h-full border border-primary/5 hover:border-primary/20 transition-all">
                  <div className="bg-primary/10 p-4 rounded-lg text-primary w-fit mb-4">
                    <Code className="h-8 w-8" />
                  </div>
                  <h3 className="text-xl font-semibold mb-3">Interactive Demos</h3>
                  <p className="text-foreground/90 leading-relaxed">
                    Real-world applications of security automation, AI-driven risk scoring, and PKI-based device security.
                  </p>
                </div>
              </SectionTransition>

              {/* Learning Resources */}
              <SectionTransition delay={400}>
                <div className="glass-card p-6 rounded-xl h-full border border-primary/5 hover:border-primary/20 transition-all">
                  <div className="bg-primary/10 p-4 rounded-lg text-primary w-fit mb-4">
                    <BookOpen className="h-8 w-8" />
                  </div>
                  <h3 className="text-xl font-semibold mb-3">Learning Resources</h3>
                  <p className="text-foreground/90 leading-relaxed">
                    Educational content designed to simplify complex regulatory requirements like GDPR and DORA, translating them into actionable engineering and product roadmaps.
                  </p>
                </div>
              </SectionTransition>
            </div>
          </div>
        </section>
      </main>

      <footer className="border-t py-6 mt-16">
        <div className="container mx-auto px-4 text-center">
          <p className="text-muted-foreground text-sm">
            © {new Date().getFullYear()} Bit2Terabyte. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default About;

